import React, { useState } from "react";

import CloseIcon from '@material-ui/icons/Close';
import SettingsIcon from '@material-ui/icons/Settings';

import { Button, Divider, Grid, IconButton, ListSubheader, makeStyles, Paper, Tooltip, Typography } from "@material-ui/core";

import { UnpaidDrawer } from './components/unpaidDrawer';
import { WritingLogDialog } from './components/writingLogDialog';
import { AnalyzerTextSuggestionsPanel } from './components/analyzerTextSuggestionsPanel';
import { AnalyzerSettingsDialog } from './components/analyzerSettingsDialog';

import DateHelper from "../../../utilities/DateHelper";
import { useEditor } from '../editor/useEditor';
import { useBuilder } from '../useBuilder';
import { EventTracking } from "../../../utilities/Tracking/EventTracking";
import { AddAssetAction } from "../../../entities/AddAssetAction";

const useStyles = makeStyles((theme) => ({
    title: {
        paddingTop: 10,
        paddingBottom: 7,
        textAlign: 'left',
        fontSize: 22
    },
    subtitle: {
        paddingBottom: 10,
        fontSize: 20,
        color: "#8c8d8f",
    },
    dividerLine: {
        margin: '5px',
        backgroundColor: '#d6d6d6',
        height: 2
    },
    closeIcon: {
        float: 'right'
    },
    settingsTitle: {
        paddingTop: theme.spacing(0),
        textAlign: "left",
        fontSize: 15,
        textOverflow: "ellipsis",
    },
    settingsIcon: {
        float: 'right',
        "&:hover": {
            backgroundColor: "rgb(145, 145, 145)"
        }
    },
    textAnalysis: {
        paddingBottom: 5,
    },
    textAnalysisResults: {
        color: "#000", 
        fontWeight: 500, 
        fontSize: '0.9rem' 
    },
    cardHeader: {
        paddingTop: 10,
        paddingBottom: 7,
        paddingLeft: 24,
        textAlign: "left",
    },
    cardContent: {
        paddingLeft: 24,
        textAlign: "left",
        marginBottom: 10,
    },
    actionButtons: {
        fontSize: 13,
        textTransform: 'capitalize'
    },
}));

export const WordsPerMinute = {
    Slow: 70,
    Average: 90,
    Fast: 110,
}

interface Props  {
    onDrawerToggle: (open: boolean) => void;
};

export function SermonAnalyzer(props: Props) {
    const { onDrawerToggle } = props
    const classes = useStyles();

    const editor = useEditor();
    const builder = useBuilder();
    const { wordCount, characterCount, improvements, goodResults, keywords, gradeLevel } = editor;
    const { builderAsset: { category }, user, writingLogs } = builder;
    
    const [wordsPerMinute, setWordsPerMinute] = useState<number>(WordsPerMinute.Average);

    const [, setOpen] = useState<boolean>(false);
    const [openAnalyzerSettings, setOpenAnalyzerSettings] = useState<boolean>(false);
    const [openWritingLogs, setOpenWritingLogs] = useState<boolean>(false)

    const setDrawerToggle = (open: boolean) => {
        setOpen(open);
        onDrawerToggle(open);
    };

    const onToggleWritingLogDialog = () => {
        if(!openAnalyzerSettings){
            EventTracking.Builder.onOpenWritingLogs("Analyzer Drawer Interaction")
        }
        setOpenWritingLogs(!openWritingLogs);
    }
    
    const onToggleSettingsDialog = () => {
        if(!openAnalyzerSettings){
            EventTracking.Builder.onOpenSettings("Analyzer Drawer Interaction")
        }
        setOpenAnalyzerSettings(!openAnalyzerSettings);
    };

    return (
        <>
            <ListSubheader className={classes.title}>
                    Sermon Analyzer
                <IconButton onClick={() => setDrawerToggle(false)} className={classes.closeIcon}>
                    <CloseIcon />
                </IconButton>
            </ListSubheader>
            <Divider />

            { 
                user && !user.makerSubscription
                    &&
                <UnpaidDrawer
                    title="Sermon Analyzer"
                    listText={["Real-time manuscript analysis", "Suggestions on improving your sermon"
                                        , "Actionable sermon metrics", "Optimize your preaching time"
                                        , "Track your sermon prep work"]} 
                    upgradeAction={AddAssetAction.Analyzer}
                    upgradePath={"Analyzer"} 
                    color='#02376b'
                    displayNumber={wordCount >= 100 ? improvements.length : 0}
                />
            }

            {
                user && user.makerSubscription
                    &&
                <>
                <ListSubheader className={classes.settingsTitle} color="primary">
                    <span style={{ cursor: "pointer" }} onClick={onToggleSettingsDialog}>
                        Customize Analyzer
                    </span>
                    <Tooltip title="Change Settings">
                    <IconButton onClick={onToggleSettingsDialog} className={classes.settingsIcon}>
                        <SettingsIcon />
                    </IconButton>
                    </Tooltip>
                </ListSubheader>
                <Divider />

                <div className='customScrollbar' style={{ overflowY: 'auto'}}>
                    <Paper elevation={0}>
                        { 
                            category === "Manuscript"
                                && 
                            <>
                                <Grid container direction="column">
                                    <div className={classes.cardHeader}>
                                        <Typography style={{display: "inline"}} className={classes.subtitle}>Preaching Time</Typography>
                                    </div>
                                    <div className={classes.cardContent}>
                                        <Typography style={{display: "inline"}} variant="h4">{Math.round(wordCount / wordsPerMinute)} Min</Typography>
                                    </div>
                                    </Grid>
                                <Divider className={classes.dividerLine} />
                            </>
                        }

                        { 
                            writingLogs.totalTime > 0
                                &&
                            <>
                                <Grid container direction="column">
                                    <div className={classes.cardHeader}>
                                        <Typography className={classes.subtitle}>Writing Time</Typography>
                                    </div>
                                    <div className={classes.cardContent} style={{paddingTop: 0, paddingBottom: 0}}>
                                    <Typography variant="h5">{DateHelper.timeFromSeconds(writingLogs.totalTime)}</Typography>
                                        {
                                            writingLogs.writingLogs.slice(0, 3).map((log, index) => {
                                                return <Typography key={index}>{DateHelper.buildDateFormat(new Date(log.logDate))} - {DateHelper.timeFromSeconds(log.logTimeInSeconds)}</Typography>
                                            })
                                        }
                                        <Button 
                                            className={classes.actionButtons}
                                            size="small" 
                                            variant="text" 
                                            color="primary"
                                            onClick={onToggleWritingLogDialog}>View Log</Button>
                                    </div>
                                </Grid>
                                <Divider className={classes.dividerLine} />
                            </>
                        }

                        <Grid container direction="column"> 
                            <div className={classes.cardHeader}>
                                <Typography className={classes.subtitle}>Text Analysis</Typography>
                            </div>
                            <div className={classes.cardContent} >
                                <Typography className={classes.textAnalysis}>
                                    Words: <span className={classes.textAnalysisResults}>{wordCount}</span>
                                </Typography>
                                <Typography className={classes.textAnalysis}>
                                    Characters: <span className={classes.textAnalysisResults}>{characterCount}</span>
                                </Typography>
                                <Typography className={classes.textAnalysis}>
                                    Education Level: 
                                    <Tooltip placement="top" title="Grade level based on the Automated Readability Index">
                                    <span className={classes.textAnalysisResults}> {gradeLevel}</span>
                                    </Tooltip>
                                </Typography>
                                <Typography className={classes.textAnalysis}>
                                    Keywords: <span className={classes.textAnalysisResults}>{ keywords.join(", ")}</span>
                                </Typography>
                            </div>
                        </Grid>

                    { 
                        wordCount >= 100
                            &&
                        <>
                        <Divider className={classes.dividerLine} />
                        <Grid container direction="column">
                            <div className={classes.cardHeader}>
                                <Typography className={classes.subtitle}>Text Improvements</Typography>
                            </div>
                            <div className={classes.cardContent}>
                                {
                                    improvements.length > 0
                                        &&
                                    <AnalyzerTextSuggestionsPanel suggestions={improvements} bulletColor='#f26321' category="Improvements"/>
                                }

                                {
                                    goodResults.length > 0
                                        &&
                                    <AnalyzerTextSuggestionsPanel suggestions={goodResults} bulletColor='#86CC44' category="Good Results"/>
                                }
                            </div>
                        </Grid>
                        </>
                    }
                    </Paper>
                </div>
                </>
            }
            {   openWritingLogs
                    &&
                <WritingLogDialog showDialog={openWritingLogs} closeDialog={onToggleWritingLogDialog}/>
            }
            {   openAnalyzerSettings
                    &&
                <AnalyzerSettingsDialog 
                    openSettings={openAnalyzerSettings} 
                    closeSettingsDialog={onToggleSettingsDialog} 
                    currentWordsPerMinute={wordsPerMinute}
                    setWordsPerMinute={setWordsPerMinute}
                />
            }
        </>
    )
};